import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, API } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FileText, Plus, Signature, Paperclip, Trash2, Clock, Pencil, X, Package } from "lucide-react";

const STATUSES = ["da_fare", "in_corso", "sospeso", "chiuso", "annullato"];

export function JobDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [reports, setReports] = useState([]);

  useEffect(() => { if (!loading && (!user || user.role === "cliente")) navigate("/login"); }, [user, loading, navigate]);
  const load = async () => {
    try {
      const j = await api.get(`/jobs/${id}`); setJob(j.data);
      const r = await api.get(`/reports?job_id=${id}`); setReports(r.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [id, user]);

  const changeStatus = async (v) => {
    try { const r = await api.patch(`/jobs/${id}`, { status: v }); setJob(r.data); toast.success("Aggiornato"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const newReport = async () => {
    try {
      const r = await api.post("/reports", { job_id: id, committente: job?.client_name, citta: job?.city, data: new Date().toISOString().slice(0, 10) });
      navigate(`/relazioni/${r.data.report_id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteJob = async () => {
    if (!window.confirm("Eliminare il lavoro e tutte le sue relazioni?")) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Eliminato"); navigate("/lavori"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!job) return <div className="min-h-screen"><Navbar/><div className="p-12 text-center text-slate-500">Caricamento...</div></div>;
  const closed = job.status === "chiuso";
  const canAdmin = user.role === "admin";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-4xl mx-auto p-6">
        <Link to="/lavori" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-800 mb-4 text-sm" data-testid="job-back"><ArrowLeft className="w-4 h-4"/>Tutti i lavori</Link>
        <Card className="p-6 mb-4">
          <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono text-slate-500">{job.job_id}</div>
              <h1 className="heading text-2xl font-extrabold text-slate-900">{job.title}</h1>
              <div className="text-sm text-slate-600 mt-1 capitalize">{job.category} · {job.city}{job.address && ` · ${job.address}`}</div>
            </div>
            <div className="flex gap-2">
              <Select value={job.status} onValueChange={changeStatus} disabled={closed && !canAdmin}>
                <SelectTrigger className="w-36" data-testid="job-status-select"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {canAdmin && <Button variant="outline" className="text-red-600" onClick={deleteJob} data-testid="job-del"><Trash2 className="w-4 h-4"/></Button>}
            </div>
          </div>
          {job.client_name && <div className="text-sm text-slate-700"><b>Cliente:</b> {job.client_name} · {job.client_contact}</div>}
          {job.scheduled_date && (
            <div className="text-sm text-blue-900 mt-1 inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded px-2 py-1">
              📅 <b>Programmato per:</b> {new Date(job.scheduled_date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
          )}
          {job.description && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{job.description}</p>}
        </Card>

        <div className="flex items-center justify-between mb-3">
          <h2 className="heading font-bold text-xl text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5"/>Relazioni RdL</h2>
          {(!closed || canAdmin) && <Button onClick={newReport} className="bg-blue-800" data-testid="report-new"><Plus className="w-4 h-4 mr-2"/>Nuova relazione</Button>}
        </div>
        {reports.length === 0 && <Card className="p-6 text-center text-slate-500 border-dashed">Nessuna relazione ancora</Card>}
        <div className="space-y-2">
          {reports.map(r => (
            <Card key={r.report_id} className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/relazioni/${r.report_id}`)}
                  data-testid={`report-row-${r.report_id}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-900">{r.data || "—"} · Intervento {r.intervento_n || "?"}</div>
                  <div className="text-xs text-slate-500">Creata da {r.created_by_name}</div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${r.status === "firmata" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>
                  {r.status}
                </span>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ReportDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [job, setJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);
  const [uploadingSig, setUploadingSig] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (!loading && (!user || user.role === "cliente")) navigate("/login"); }, [user, loading, navigate]);

  const load = async () => {
    try {
      const x = await api.get(`/reports/${id}`); setR(x.data);
      const j = await api.get(`/jobs/${x.data.job_id}`); setJob(j.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [id, user]);

  const save = async () => {
    if (!r) return;
    setSaving(true);
    try {
      const patch = { ...r };
      delete patch.report_id; delete patch.status; delete patch.created_by; delete patch.created_by_name; delete patch.created_at; delete patch.updated_at; delete patch.updated_by; delete patch.signed_at;
      await api.patch(`/reports/${id}`, patch);
      toast.success("Salvato");
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const saveSignature = async () => {
    if (sigRef.current.isEmpty()) return toast.error("Nessuna firma");
    setUploadingSig(true);
    try {
      // Auto-save: first persist the currently-edited form fields so the user doesn't lose unsaved typing
      const patch = { ...r };
      ["report_id","status","created_by","created_by_name","created_at","updated_at","updated_by","signed_at","firma_file_id","work_entries","materials","allegati"].forEach(k => delete patch[k]);
      await api.patch(`/reports/${id}`, patch);
      // Then upload signature + mark firmata
      const dataUrl = sigRef.current.toDataURL("image/png");
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("file", blob, "firma.png");
      const up = await api.post("/uploads", fd);
      await api.patch(`/reports/${id}`, { firma_file_id: up.data.file_id });
      toast.success("Firma salvata (campi auto-salvati)");
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setUploadingSig(false); }
  };

  const attachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd);
      const allegati = [...(r.allegati || []), up.data.file_id];
      await api.patch(`/reports/${id}`, { allegati });
      toast.success("Allegato caricato");
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setAttaching(false); e.target.value = ""; }
  };

  if (!r) return <div className="min-h-screen"><Navbar/><div className="p-12 text-center text-slate-500">Caricamento...</div></div>;
  const readOnly = r.status === "firmata" && user.role !== "admin";
  const isTabletMode = typeof window !== "undefined" && !!localStorage.getItem("energyworld.device_token");

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-3xl mx-auto p-6">
        <Link to={`/lavori/${r.job_id}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-800 mb-4 text-sm"><ArrowLeft className="w-4 h-4"/>Torna al lavoro</Link>
        <Card className="p-6 mb-4">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Rapportino di Lavoro</div>
              <h1 className="heading text-2xl font-extrabold text-slate-900">RdL · {r.report_id}</h1>
            </div>
            <div className="flex items-center gap-2">
              {(user.role === "admin" || user.role === "personale") && !isTabletMode && (
                <a href={`${API}/reports/${r.report_id}/invoice-pdf`} target="_blank" rel="noreferrer"
                   data-testid="rdl-invoice-btn">
                  <Button variant="outline" className="border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900">
                    <FileText className="w-4 h-4 mr-2"/> Emetti fattura PDF
                  </Button>
                </a>
              )}
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${r.status === "firmata" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>{r.status}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <F label="Intervento n°" value={r.intervento_n} onChange={v=>setR({...r,intervento_n:v})} disabled={readOnly} test="intervento-n"/>
            <F label="Commessa n°" value={r.commessa_n} onChange={v=>setR({...r,commessa_n:v})} disabled={readOnly}/>
            <F label="Data" type="date" value={r.data} onChange={v=>setR({...r,data:v})} disabled={readOnly}/>
            <F label="Committente" value={r.committente} onChange={v=>setR({...r,committente:v})} disabled={readOnly}/>
            <F label="Via" value={r.via} onChange={v=>setR({...r,via:v})} disabled={readOnly}/>
            <F label="Città" value={r.citta} onChange={v=>setR({...r,citta:v})} disabled={readOnly}/>
            <F label="Telefono" value={r.telefono} onChange={v=>setR({...r,telefono:v})} disabled={readOnly}/>
            <F label="C.F./P.I." value={r.cf_pi} onChange={v=>setR({...r,cf_pi:v})} disabled={readOnly}/>
            <F label="Email" value={r.email} onChange={v=>setR({...r,email:v})} disabled={readOnly}/>
            <F label="Fine intervento" value={r.fine_intervento} onChange={v=>setR({...r,fine_intervento:v})} disabled={readOnly}/>
          </div>

          <div className="mt-4">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Descrizione intervento</label>
            <textarea className="w-full min-h-[120px] rounded-md border border-slate-300 p-3 text-sm" value={r.descrizione_intervento || ""} onChange={e=>setR({...r,descrizione_intervento:e.target.value})} disabled={readOnly} data-testid="descrizione"/>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 items-start">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2 block">Prove di funzionamento</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.prove_positivo} onChange={e=>setR({...r,prove_positivo:e.target.checked,prove_negativo:e.target.checked?false:r.prove_negativo})} disabled={readOnly}/>Positivo</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.prove_negativo} onChange={e=>setR({...r,prove_negativo:e.target.checked,prove_positivo:e.target.checked?false:r.prove_positivo})} disabled={readOnly}/>Negativo</label>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2 block">Tipo contratto</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.contratto} onChange={e=>setR({...r,contratto:e.target.checked})} disabled={readOnly}/>Contratto</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.garanzia} onChange={e=>setR({...r,garanzia:e.target.checked})} disabled={readOnly}/>Garanzia</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.extra_contratto} onChange={e=>setR({...r,extra_contratto:e.target.checked})} disabled={readOnly}/>Extra contratto</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.economia} onChange={e=>setR({...r,economia:e.target.checked})} disabled={readOnly}/>Economia</label>
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Note prove funzionamento</label>
            <textarea className="w-full min-h-[60px] rounded-md border border-slate-300 p-3 text-sm" value={r.prove_note || ""} onChange={e=>setR({...r,prove_note:e.target.value})} disabled={readOnly}/>
          </div>

          {!readOnly && (
            <div className="mt-4 flex gap-2 flex-wrap">
              <Button onClick={save} disabled={saving} className="bg-blue-800" data-testid="rdl-save">{saving ? <Loader2 className="w-4 h-4 animate-spin"/> : "Salva"}</Button>
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={attaching} data-testid="rdl-attach"><Paperclip className="w-4 h-4 mr-2"/>Allega file</Button>
              <input ref={fileRef} type="file" className="hidden" onChange={attachFile}/>
            </div>
          )}
        </Card>

        {r.allegati?.length > 0 && (
          <Card className="p-4 mb-4">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2">Allegati</h3>
            <div className="space-y-1">
              {r.allegati.map(fid => (
                <a key={fid} href={`${API}/files/${fid}`} target="_blank" rel="noreferrer" className="text-sm text-blue-800 hover:underline block">📎 {fid}</a>
              ))}
            </div>
          </Card>
        )}

        <WorkEntriesSection report={r} job={job} user={user} onChanged={load} readOnly={readOnly}/>

        <MaterialsSection report={r} job={job} user={user} onChanged={load} readOnly={readOnly}/>

        <Card className="p-6">
          <h3 className="heading font-bold text-slate-900 mb-3 flex items-center gap-2"><Signature className="w-5 h-5"/>Firma del cliente per accettazione</h3>
          {r.firma_file_id ? (
            <div>
              <img src={`${API}/files/${r.firma_file_id}`} alt="firma" className="max-h-32 border rounded bg-white"/>
              {r.signed_at && <p className="text-xs text-slate-500 mt-2">Firmato il {new Date(r.signed_at).toLocaleString("it-IT")}</p>}
            </div>
          ) : readOnly ? (
            <p className="text-sm text-slate-500">Nessuna firma</p>
          ) : (
            <div>
              <div className="border-2 border-dashed border-slate-300 rounded-md bg-white touch-none">
                <SignatureCanvas ref={sigRef} canvasProps={{ className: "w-full h-40", "data-testid": "signature-canvas", willReadFrequently: true }} penColor="#0f172a"/>
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" onClick={() => sigRef.current?.clear()} data-testid="signature-clear">Pulisci</Button>
                <Button onClick={saveSignature} disabled={uploadingSig} className="bg-emerald-600 hover:bg-emerald-700" data-testid="signature-save">
                  {uploadingSig ? <Loader2 className="w-4 h-4 animate-spin"/> : "Salva firma"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">Al salvataggio della firma la relazione viene marcata come "firmata" e non potrà più essere modificata (tranne dall'admin).</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function F({ label, value, onChange, disabled, type = "text", test }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">{label}</label>
      <Input type={type} value={value || ""} onChange={e=>onChange(e.target.value)} disabled={disabled} data-testid={test}/>
    </div>
  );
}

function WorkEntriesSection({ report, job, user, onChanged, readOnly }) {
  const [operai, setOperai] = useState([]);
  const [rates, setRates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // entry_id being edited (null = none, "new" = new row form)
  const [form, setForm] = useState({ operaio_id: "", date: new Date().toISOString().slice(0, 10), hours: "", notes: "" });
  const entries = report.work_entries || [];
  const isTablet = typeof window !== "undefined" && !!localStorage.getItem("energyworld.device_token");
  // Hide labor cost columns on tablet even for admin/staff (privacy in cantiere)
  const canSeeCosts = !isTablet && (user.role === "admin" || user.role === "personale" || (user.role === "operaio" && user.is_responsible));

  useEffect(() => {
    api.get("/operai").then(x => setOperai(x.data)).catch(()=>{});
    if (user.role === "admin" || user.role === "personale" || user.role === "operaio") {
      api.get("/hourly-rates").then(x => setRates(x.data)).catch(()=>{});
    }
  }, [user.role]);

  const rateFor = (operaio_id) => {
    const op = operai.find(o => o.user_id === operaio_id);
    if (!op || !op.worker_category || !op.worker_subcategory) return null;
    const r = rates.find(rr => rr.category === op.worker_category && rr.subcategory === op.worker_subcategory);
    return r ? Number(r.hourly_rate) : null;
  };
  const costFor = (entry) => {
    const rate = rateFor(entry.operaio_id);
    if (rate == null) return null;
    return Number(entry.hours || 0) * rate;
  };

  const canEditEntry = (e) => {
    if (readOnly) return false;
    if (user.role === "admin" || user.role === "personale") return true;
    if (job?.status === "chiuso" || report.status === "firmata") return false;
    if (user.role === "operaio") {
      if (user.is_responsible && job?.category === user.worker_category) return true;
      return e.created_by === user.user_id;
    }
    return false;
  };

  const startNew = () => {
    setForm({
      operaio_id: user.role === "operaio" ? user.user_id : (operai[0]?.user_id || ""),
      date: new Date().toISOString().slice(0, 10),
      hours: "",
      notes: "",
    });
    setEditing("new");
  };
  const startEdit = (e) => {
    setForm({ operaio_id: e.operaio_id, date: e.date, hours: String(e.hours), notes: e.notes || "" });
    setEditing(e.entry_id);
  };
  const cancel = () => { setEditing(null); };

  const save = async () => {
    if (!form.operaio_id) { toast.error("Seleziona un operaio"); return; }
    if (!form.date) { toast.error("Seleziona una data"); return; }
    const hours = parseFloat(form.hours);
    if (!hours || hours <= 0 || hours > 24) { toast.error("Ore non valide (0-24)"); return; }
    setBusy(true);
    try {
      const payload = { operaio_id: form.operaio_id, date: form.date, hours, notes: form.notes };
      if (editing === "new") {
        await api.post(`/reports/${report.report_id}/entries`, payload);
        toast.success("Riga aggiunta");
      } else {
        await api.patch(`/reports/${report.report_id}/entries/${editing}`, payload);
        toast.success("Riga aggiornata");
      }
      setEditing(null);
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const remove = async (entry_id) => {
    if (!window.confirm("Eliminare questa riga?")) return;
    try {
      await api.delete(`/reports/${report.report_id}/entries/${entry_id}`);
      toast.success("Eliminata");
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const totalHours = entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const totalCost = entries.reduce((s, e) => {
    const c = costFor(e);
    return s + (c == null ? 0 : c);
  }, 0);
  const anyMissingRate = canSeeCosts && entries.some(e => rateFor(e.operaio_id) == null);
  const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card className="p-4 mb-4" data-testid="work-entries-section">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="heading font-bold text-slate-900 flex items-center gap-2"><Clock className="w-5 h-5"/>Ore lavorate</h3>
        {!readOnly && editing !== "new" && (
          <Button size="sm" onClick={startNew} className="bg-blue-800" data-testid="entry-add-btn"><Plus className="w-4 h-4 mr-1"/>Aggiungi riga</Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">Ogni operaio può inserire una riga anche per un collega (tablet condiviso). Le righe possono essere modificate o cancellate solo dal loro autore (o dal responsabile/personale/admin).{canSeeCosts && " Il costo mano d'opera è calcolato con le tariffe orarie (categoria/sottocategoria dell'operaio)."}</p>

      {editing === "new" && (
        <EntryForm form={form} setForm={setForm} operai={operai} user={user} onSave={save} onCancel={cancel} busy={busy} isNew/>
      )}

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Operaio</th>
              <th className="text-left px-3 py-2 w-28">Data</th>
              <th className="text-right px-3 py-2 w-16">Ore</th>
              {canSeeCosts && <th className="text-right px-3 py-2 w-20">€/h</th>}
              {canSeeCosts && <th className="text-right px-3 py-2 w-24">Costo</th>}
              <th className="text-left px-3 py-2">Note</th>
              <th className="text-left px-3 py-2 w-32">Inserita da</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody data-testid="work-entries-list">
            {entries.length === 0 && editing !== "new" && (
              <tr><td colSpan={canSeeCosts ? 8 : 6} className="text-center py-6 text-slate-400 text-sm">Nessuna riga. Aggiungine una per registrare le ore.</td></tr>
            )}
            {entries.map(e => {
              const rate = rateFor(e.operaio_id);
              const cost = rate == null ? null : (Number(e.hours) || 0) * rate;
              return editing === e.entry_id ? (
                <tr key={e.entry_id} className="border-t bg-blue-50">
                  <td colSpan={canSeeCosts ? 8 : 6} className="px-3 py-2">
                    <EntryForm form={form} setForm={setForm} operai={operai} user={user} onSave={save} onCancel={cancel} busy={busy}/>
                  </td>
                </tr>
              ) : (
                <tr key={e.entry_id} className="border-t hover:bg-slate-50" data-testid={`entry-row-${e.entry_id}`}>
                  <td className="px-3 py-2 font-medium">{e.operaio_name}</td>
                  <td className="px-3 py-2 text-slate-600">{e.date}</td>
                  <td className="px-3 py-2 text-right font-medium">{Number(e.hours).toFixed(2)}</td>
                  {canSeeCosts && <td className="px-3 py-2 text-right text-slate-600">{rate == null ? <span className="text-slate-300" title="Tariffa non impostata">—</span> : fmtEur(rate)}</td>}
                  {canSeeCosts && <td className="px-3 py-2 text-right font-medium text-slate-800">{cost == null ? <span className="text-slate-300">—</span> : fmtEur(cost)}</td>}
                  <td className="px-3 py-2 text-slate-700 text-xs">{e.notes || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{e.created_by_name}</td>
                  <td className="px-2 py-2">
                    {canEditEntry(e) && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => startEdit(e)} className="p-1 text-slate-500 hover:text-blue-800" title="Modifica" data-testid={`entry-edit-${e.entry_id}`}><Pencil className="w-4 h-4"/></button>
                        <button onClick={() => remove(e.entry_id)} className="p-1 text-slate-500 hover:text-red-600" title="Elimina" data-testid={`entry-del-${e.entry_id}`}><Trash2 className="w-4 h-4"/></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {entries.length > 0 && (
              <>
                <tr className="border-t bg-slate-100">
                  <td colSpan={2} className="px-3 py-2 text-right font-semibold text-slate-700">Totale ore</td>
                  <td className="px-3 py-2 text-right font-bold text-blue-900" data-testid="entries-total">{totalHours.toFixed(2)}</td>
                  {canSeeCosts && <td></td>}
                  {canSeeCosts && <td></td>}
                  <td colSpan={3}></td>
                </tr>
                {canSeeCosts && (
                  <tr className="border-t bg-emerald-50">
                    <td colSpan={2} className="px-3 py-2 text-right font-semibold text-emerald-900">Totale mano d'opera</td>
                    <td colSpan={3} className="px-3 py-2 text-right font-bold text-emerald-900 text-base" data-testid="entries-cost-total">
                      {fmtEur(totalCost)}
                    </td>
                    <td colSpan={3} className="px-3 py-2 text-xs text-emerald-800 italic">
                      {anyMissingRate && <span title="Alcune tariffe non sono impostate">⚠ Alcune tariffe mancano — <Link to="/admin/tariffe" className="underline">imposta</Link></span>}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function EntryForm({ form, setForm, operai, user, onSave, onCancel, busy, isNew }) {
  return (
    <div className={`${isNew ? "border rounded-md bg-blue-50 p-3 mb-3" : ""} grid grid-cols-1 md:grid-cols-12 gap-2 items-end`} data-testid="entry-form">
      <div className="md:col-span-4">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Operaio</label>
        <Select value={form.operaio_id} onValueChange={v => setForm(f => ({...f, operaio_id: v}))}>
          <SelectTrigger className="h-9" data-testid="entry-operaio"><SelectValue placeholder="Seleziona operaio"/></SelectTrigger>
          <SelectContent>
            {operai.map(o => <SelectItem key={o.user_id} value={o.user_id}>{o.name}{o.user_id === user.user_id ? " (io)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Data</label>
        <Input type="date" value={form.date} onChange={e=>setForm(f=>({...f, date: e.target.value}))} className="h-9" data-testid="entry-date"/>
      </div>
      <div className="md:col-span-1">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Ore</label>
        <Input type="number" step="0.25" min="0" max="24" value={form.hours} onChange={e=>setForm(f=>({...f, hours: e.target.value}))} className="h-9" data-testid="entry-hours"/>
      </div>
      <div className="md:col-span-3">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Note (opzionali)</label>
        <Input value={form.notes} onChange={e=>setForm(f=>({...f, notes: e.target.value}))} className="h-9" placeholder="Es. sostituzione compressore" data-testid="entry-notes"/>
      </div>
      <div className="md:col-span-2 flex gap-1 justify-end">
        <Button onClick={onSave} disabled={busy} className="bg-blue-800 h-9" data-testid="entry-save">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Salva"}</Button>
        <Button variant="outline" onClick={onCancel} className="h-9" data-testid="entry-cancel"><X className="w-4 h-4"/></Button>
      </div>
    </div>
  );
}

const MAT_UNITS = ["pz", "m", "m2", "kg", "l", "conf", "h"];
const fmtEurLocal = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MaterialsSection({ report, job, user, onChanged, readOnly }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // material_id being edited, or "new"
  const [form, setForm] = useState({ description: "", quantity: "1", unit: "pz", unit_price: "", notes: "" });
  const materials = report.materials || [];
  const isTablet = typeof window !== "undefined" && !!localStorage.getItem("energyworld.device_token");
  // On tablet hide only the aggregate total; individual prices stay visible so operai can enter them
  const showTotal = !isTablet;

  const canEditMat = (m) => {
    if (readOnly) return false;
    if (user.role === "admin" || user.role === "personale") return true;
    if (job?.status === "chiuso" || report.status === "firmata") return false;
    if (user.role === "operaio") {
      if (user.is_responsible && job?.category === user.worker_category) return true;
      return m.created_by === user.user_id;
    }
    return false;
  };

  const startNew = () => {
    setForm({ description: "", quantity: "1", unit: "pz", unit_price: "", notes: "" });
    setEditing("new");
  };
  const startEdit = (m) => {
    setForm({ description: m.description, quantity: String(m.quantity), unit: m.unit || "pz",
              unit_price: String(m.unit_price ?? ""), notes: m.notes || "" });
    setEditing(m.material_id);
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!form.description.trim()) { toast.error("Descrizione obbligatoria"); return; }
    const q = parseFloat(form.quantity);
    if (!q || q <= 0) { toast.error("Quantità non valida"); return; }
    const p = parseFloat(form.unit_price || "0");
    if (Number.isNaN(p) || p < 0) { toast.error("Prezzo non valido"); return; }
    setBusy(true);
    try {
      const payload = { description: form.description.trim(), quantity: q, unit: form.unit, unit_price: p, notes: form.notes };
      if (editing === "new") {
        await api.post(`/reports/${report.report_id}/materials`, payload);
        toast.success("Materiale aggiunto");
      } else {
        await api.patch(`/reports/${report.report_id}/materials/${editing}`, payload);
        toast.success("Materiale aggiornato");
      }
      setEditing(null);
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const remove = async (mid) => {
    if (!window.confirm("Eliminare questa riga?")) return;
    try {
      await api.delete(`/reports/${report.report_id}/materials/${mid}`);
      toast.success("Eliminata");
      onChanged && onChanged();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const total = materials.reduce((s, m) => s + (Number(m.quantity) || 0) * (Number(m.unit_price) || 0), 0);

  return (
    <Card className="p-4 mb-4" data-testid="materials-section">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="heading font-bold text-slate-900 flex items-center gap-2"><Package className="w-5 h-5"/>Materiali utilizzati</h3>
        {!readOnly && editing !== "new" && (
          <Button size="sm" onClick={startNew} className="bg-blue-800" data-testid="mat-add-btn"><Plus className="w-4 h-4 mr-1"/>Aggiungi materiale</Button>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-3">Inserisci i materiali consumati durante l'intervento. Utile per le uscite senza preventivo: alla fine hai il totale da fatturare.</p>

      {editing === "new" && (
        <MaterialForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy} isNew/>
      )}

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Descrizione</th>
              <th className="text-right px-3 py-2 w-16">Q.tà</th>
              <th className="text-left px-3 py-2 w-20">U.M.</th>
              <th className="text-right px-3 py-2 w-24">Prezzo</th>
              <th className="text-right px-3 py-2 w-28">Importo</th>
              <th className="text-left px-3 py-2">Note</th>
              <th className="text-left px-3 py-2 w-32">Inserita da</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody data-testid="materials-list">
            {materials.length === 0 && editing !== "new" && (
              <tr><td colSpan={8} className="text-center py-6 text-slate-400 text-sm">Nessun materiale registrato.</td></tr>
            )}
            {materials.map(m => {
              const imp = (Number(m.quantity) || 0) * (Number(m.unit_price) || 0);
              return editing === m.material_id ? (
                <tr key={m.material_id} className="border-t bg-blue-50">
                  <td colSpan={8} className="px-3 py-2">
                    <MaterialForm form={form} setForm={setForm} onSave={save} onCancel={cancel} busy={busy}/>
                  </td>
                </tr>
              ) : (
                <tr key={m.material_id} className="border-t hover:bg-slate-50" data-testid={`mat-row-${m.material_id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{m.description}</td>
                  <td className="px-3 py-2 text-right">{Number(m.quantity).toFixed(2)}</td>
                  <td className="px-3 py-2 text-slate-600">{m.unit}</td>
                  <td className="px-3 py-2 text-right text-slate-700">{fmtEurLocal(m.unit_price)}</td>
                  <td className="px-3 py-2 text-right font-medium text-slate-800">{fmtEurLocal(imp)}</td>
                  <td className="px-3 py-2 text-slate-700 text-xs">{m.notes || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{m.created_by_name}</td>
                  <td className="px-2 py-2">
                    {canEditMat(m) && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => startEdit(m)} className="p-1 text-slate-500 hover:text-blue-800" title="Modifica" data-testid={`mat-edit-${m.material_id}`}><Pencil className="w-4 h-4"/></button>
                        <button onClick={() => remove(m.material_id)} className="p-1 text-slate-500 hover:text-red-600" title="Elimina" data-testid={`mat-del-${m.material_id}`}><Trash2 className="w-4 h-4"/></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {materials.length > 0 && showTotal && (
              <tr className="border-t bg-emerald-50">
                <td colSpan={4} className="px-3 py-2 text-right font-semibold text-emerald-900">Totale materiali</td>
                <td className="px-3 py-2 text-right font-bold text-emerald-900 text-base" data-testid="materials-total">{fmtEurLocal(total)}</td>
                <td colSpan={3}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function MaterialForm({ form, setForm, onSave, onCancel, busy, isNew }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [saveToCatalog, setSaveToCatalog] = useState(false);

  useEffect(() => {
    const q = (form.description || "").trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      api.get(`/material-catalog?q=${encodeURIComponent(q)}&limit=8`)
        .then(r => setSuggestions(r.data || []))
        .catch(() => setSuggestions([]));
    }, 220);
    return () => clearTimeout(t);
  }, [form.description]);

  const pickSuggestion = (c) => {
    setForm(f => ({ ...f, description: c.description, unit: c.unit || "pz", unit_price: String(c.default_price || 0) }));
    setShowSuggest(false);
  };

  const saveWithCatalog = async () => {
    // Optionally save to catalog BEFORE saving the row — fire and forget, not fatal if fails
    if (saveToCatalog && form.description.trim()) {
      try {
        await api.post("/material-catalog", {
          description: form.description.trim(),
          unit: form.unit, default_price: parseFloat(form.unit_price || "0") || 0,
        });
        toast.success("Aggiunto al catalogo");
      } catch { /* non-fatal */ }
    }
    onSave();
  };

  return (
    <div className={`${isNew ? "border rounded-md bg-blue-50 p-3 mb-3" : ""} grid grid-cols-1 md:grid-cols-12 gap-2 items-end`} data-testid="mat-form">
      <div className="md:col-span-4 relative">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Descrizione</label>
        <Input value={form.description} onChange={e=>{setForm(f=>({...f, description: e.target.value})); setShowSuggest(true);}}
               onFocus={()=>setShowSuggest(true)}
               onBlur={()=>setTimeout(()=>setShowSuggest(false), 180)}
               className="h-9" placeholder="Es. Lampadina LED 10W" data-testid="mat-description"/>
        {showSuggest && suggestions.length > 0 && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto" data-testid="mat-suggestions">
            {suggestions.map(s => (
              <button key={s.catalog_id} type="button" onMouseDown={e=>e.preventDefault()}
                      onClick={() => pickSuggestion(s)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                      data-testid={`mat-sugg-${s.catalog_id}`}>
                <div className="text-sm font-medium text-slate-800">{s.description}</div>
                <div className="text-xs text-slate-500">{s.unit} · € {Number(s.default_price || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="md:col-span-1">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Q.tà</label>
        <Input type="number" step="0.5" min="0" value={form.quantity} onChange={e=>setForm(f=>({...f, quantity: e.target.value}))} className="h-9 text-right" data-testid="mat-quantity"/>
      </div>
      <div className="md:col-span-1">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">U.M.</label>
        <Select value={form.unit} onValueChange={v=>setForm(f=>({...f, unit: v}))}>
          <SelectTrigger className="h-9" data-testid="mat-unit"><SelectValue/></SelectTrigger>
          <SelectContent>{MAT_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Prezzo unit. (€)</label>
        <Input type="number" step="0.01" min="0" value={form.unit_price} onChange={e=>setForm(f=>({...f, unit_price: e.target.value}))} className="h-9 text-right" placeholder="0,00" data-testid="mat-price"/>
      </div>
      <div className="md:col-span-2">
        <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Note</label>
        <Input value={form.notes} onChange={e=>setForm(f=>({...f, notes: e.target.value}))} className="h-9" placeholder="Opzionale" data-testid="mat-notes"/>
      </div>
      <div className="md:col-span-2 flex gap-1 justify-end">
        <Button onClick={saveWithCatalog} disabled={busy} className="bg-blue-800 h-9" data-testid="mat-save">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Salva"}</Button>
        <Button variant="outline" onClick={onCancel} className="h-9" data-testid="mat-cancel"><X className="w-4 h-4"/></Button>
      </div>
      {isNew && (
        <div className="md:col-span-12 flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" id="mat-save-cat" checked={saveToCatalog} onChange={e=>setSaveToCatalog(e.target.checked)}
                 className="rounded border-slate-300" data-testid="mat-save-to-catalog"/>
          <label htmlFor="mat-save-cat" className="cursor-pointer">Aggiungi anche al catalogo prezziario (riutilizzabile in futuro)</label>
        </div>
      )}
    </div>
  );
}

